import React, { useEffect } from 'react';
import { MapPin } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../../contexts/ThemeContext';
import LocationSelect from './LocationSelect';
import { shortenAddress } from '../../../utils/bookingTimeUtils';
import { haveSameLocationAddress } from '../../../utils/bookingLocationUtils';

/**
 * LocationSelectionSection Component
 *
 * Displays pickup/dropoff location selectors and route overview.
 * Only shows if route has intermediate stops (more than 2 stops).
 * Shows boarding info buttons for selected pickup/dropoff locations.
 *
 * State Ownership:
 * - Reads all props from parent (no local state)
 * - Location state managed by parent component
 *
 * @param {Object} props
 * @param {Object} props.currentRoute - Current route object with waypoints
 * @param {string} props.pickupValue - Selected pickup stop ID
 * @param {string} props.dropoffValue - Selected dropoff stop ID
 * @param {Function} props.onChange - Handler for location changes
 * @param {Function} props.onOpenBoardingInfo - Handler to open boarding info modal
 * @param {Function} props.hasBoardingInfo - Function to check if waypoint has boarding info
 */
const LocationSelectionSection = ({
  currentRoute,
  pickupValue,
  dropoffValue,
  onChange,
  onOpenBoardingInfo,
  hasBoardingInfo
}) => {
  const { t } = useTranslation();
  const { background, text, cn } = useTheme();

  const stops = (currentRoute?.waypoints || [])
    .filter(waypoint => waypoint.point_type === 'stop')
    .sort((a, b) => a.sequence_number - b.sequence_number);
  const shouldRenderLocationSelectors = Boolean(currentRoute?.waypoints) && stops.length > 2;

  // Prepare pickup locations
  const pickupLocations = stops
    .filter(waypoint => waypoint.can_pickup)
    .map(waypoint => ({
      id: waypoint.id,
      name: waypoint.sequence_number === 0 ? t('Start') : waypoint.name,
      address: shortenAddress(waypoint.address),
      type: waypoint.sequence_number === 0 ? 'start' : 'stop'
    }));

  // Prepare dropoff locations (must come after selected pickup)
  const lastStop = stops[stops.length - 1];
  const selectedPickup = stops.find(waypoint => String(waypoint.id) === String(pickupValue));
  const pickupSequence = selectedPickup?.sequence_number ?? -1;

  const dropoffLocations = stops
    .filter(waypoint =>
      waypoint.can_dropoff &&
      waypoint.sequence_number > pickupSequence &&
      !haveSameLocationAddress(waypoint, selectedPickup)
    )
    .map(waypoint => ({
      id: waypoint.id,
      name: waypoint.id === lastStop?.id ? t('end') : waypoint.name,
      address: shortenAddress(waypoint.address),
      type: waypoint.id === lastStop?.id ? 'end' : 'stop'
    }));

  useEffect(() => {
    if (!shouldRenderLocationSelectors) {
      return;
    }

    const hasSelectedPickup = pickupLocations.some(
      (location) => String(location.id) === String(pickupValue),
    );

    if (pickupLocations.length === 1 && !hasSelectedPickup) {
      onChange({ target: { name: 'pickup', value: pickupLocations[0].id } });
      return;
    }

    if (!hasSelectedPickup) {
      return;
    }

    const hasSelectedDropoff = dropoffLocations.some(
      (location) => String(location.id) === String(dropoffValue),
    );

    if (dropoffLocations.length === 1 && !hasSelectedDropoff) {
      onChange({ target: { name: 'dropoff', value: dropoffLocations[0].id } });
    }
  }, [dropoffLocations, dropoffValue, onChange, pickupLocations, pickupValue, shouldRenderLocationSelectors]);

  // Only show if there are intermediate stops (more than 2 stops)
  if (!shouldRenderLocationSelectors) {
    return null;
  }

  return (
    <>
      {/* Pickup and Dropoff Selection */}
      <LocationSelect
        label={t('pickupLocation')}
        value={pickupValue}
        onChange={onChange}
        locations={pickupLocations}
        type="pickup"
        theme={{ cn, background, text }}
      />

      <LocationSelect
        label={t('dropoffLocation')}
        value={dropoffValue}
        onChange={onChange}
        locations={dropoffLocations}
        type="dropoff"
        theme={{ cn, background, text }}
      />

      {/* Route Overview */}
      <div className={cn("mt-4 p-3 rounded-md", background.tertiary)}>
        <h4 className={cn("text-sm font-medium mb-2", text.primary)}>
          {t('routeOverview')}
        </h4>
        <div className="space-y-2 text-sm">
          {stops.map((waypoint, index, array) => {
            const isStart = index === 0;
            const isEnd = index === array.length - 1;
            const isPickup = pickupValue === waypoint.id;
            const isDropoff = dropoffValue === waypoint.id;

            return (
              <div key={waypoint.id} className="group relative flex items-start">
                <div className={`w-2 h-2 mt-1.5 rounded-full ${
                  isStart ? 'bg-green-500' :
                  isEnd ? 'bg-red-500' :
                  'bg-blue-500'
                }`}></div>
                <div className="ml-2 flex-1">
                  <div className={cn("flex items-center justify-between")}>
                    <div>
                      <div className={cn(
                        "flex items-center",
                        isPickup ? 'text-blue-500 font-bold' :
                        isDropoff ? 'text-red-500 font-bold' :
                        text.secondary
                      )}>
                        {isStart ? t('Start')+': ' :
                        isEnd ? t('End')+': ' :
                        ''}
                        {`${waypoint.name}`+' '}
                        {isPickup ? t('(pickup)') :
                         isDropoff ? t('(dropoff)') : ''}
                      </div>
                      <div className={cn(
                        isPickup ? 'text-blue-500 font-bold' :
                        isDropoff ? 'text-red-500 font-bold' :
                        text.secondary
                      )}>
                        {shortenAddress(waypoint.address)}
                      </div>
                      <div>
                        {hasBoardingInfo(waypoint) && (isPickup || isDropoff) && (
                          <button
                            type="button"
                            onClick={() => onOpenBoardingInfo(waypoint)}
                            className={cn(
                              "inline-flex items-center gap-1 mt-1 px-2 py-0.5 text-xm font-medium rounded transition-colors",
                              "hover:bg-blue-100 dark:hover:bg-blue-900/30",
                              "text-blue-500"
                            )}
                            title={t('boardingInfoAvailable')}
                          >
                            <MapPin className="w-4 h-4" />
                            {t('findLocation')}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
};


export default React.memo(LocationSelectionSection);
